/**
 * @description 腾讯视频phone端的代码实现
 * @author aprilcai
 */

import { resultCode, resultMessage, wrapResult } from '../module/entity';
import { invokeAsync } from '../module/bridge';
import previewShareImage from './ui/preview-share-image';
import {
  mobileLogin,
  mobileAccessLocal,
  mobileClose,
  mobileGetUserInfo,
  mobileGetDeviceId,
  mobileGetNetworkState,
  mobileOpenWebview,
  mobileOpenPayPage,
  mobileShare,
  mobileWriteLog,
  mobileOpenPayVipView,
  mobileToast,
} from './pad-phone-common-method';
import { loadNoSleepModule } from '../module/util';
import { isIPhone, isHuawei, isTBS, isAndroid, isTenvideoPhone } from '../module/ua';

// 当前webview是否支持
export function support() {
  return isTenvideoPhone;
}

// 登录
export async function login(): Promise<IResult> {
  return mobileLogin();
}

// 退出登录
export async function logout(): Promise<IResult> {
  const res = await invokeAsync('actionLogin', { type: 'tv' });
  const { errCode, errMsg, result } = res;
  if (result.actionType === 'logout') {
    return wrapResult(errCode, errMsg, {
      isLogin: result === 'qq' || result === 'wx',
    });
  }
  return wrapResult(resultCode.error, `${resultMessage.error}, expect result.actionType to be 'logout', received: '${result.actionType}'`);
}

// 是否登录
export async function isLogin(): Promise<IResult> {
  const res = await invokeAsync('getMainLoginType', null);
  const { errCode, errMsg, result } = res;
  return wrapResult(errCode, errMsg, {
    isLogin: result === 'qq' || result === 'wx',
  });
}

// 拉起第三方应用
export async function launch3rdApp(param: { pkgName: string; pkgUrl: string }): Promise<IResult> {
  const result = await invokeAsync('launch3rdApp', param);
  const { errCode, errMsg } = result;
  if (errCode === 0) {
    return wrapResult(resultCode.ok, resultMessage.ok);
  }
  return wrapResult(resultCode.error, `${resultMessage.error}: ${errMsg}`);
}

// 本地存储
export async function accessLocal(param: {
  type: string;
  key: string;
  value: string;
}): Promise<IResult> {
  return mobileAccessLocal(param);
}

// 添加日历通知
export async function addCalendarNotice(param: {
  title: string;
  startTime: string;
  location: string;
  allDay: number;
  alarmStr: string;
  notes: string;
  url: string;
}): Promise<IResult> {
  const checkPermissionResult = await invokeAsync('checkSystemCalendarAccessble', null);
  const permissionResult = checkPermissionResult.result;
  if (permissionResult !== 0 && permissionResult !== 1) {
    return wrapResult(resultCode.notAllow, resultMessage.notAllow);
  }
  const addCalendarNoticeResult = await invokeAsync('createCalendarEvent', param);
  const { errCode } = addCalendarNoticeResult;
  if (errCode !== 0) {
    return wrapResult(resultCode.error, `${resultMessage.error}(${errCode})`);
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 添加到桌面快捷方式
export async function addShortcut(param: {
  title: string;
  icon: string;
  url: string;
  h5url?: string;
}): Promise<IResult> {
  const paramData = param;
  if (isIPhone) {
    paramData.h5url = param.url;
  } else {
    paramData.url = `txvideo://v.qq.com/Html5Activity?from=30170&url=${encodeURIComponent(param.url)}`;
  }
  const apiResult = await invokeAsync('addShortcut', paramData);
  const { errCode } = apiResult;
  if (errCode !== 0) {
    return wrapResult(resultCode.error, `${resultMessage.error}(${errCode})`);
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 保存图片
export async function saveImage(param: { url: string }): Promise<IResult> {
  const p: { [key: string]: string } = {};
  const { url } = param;
  if (/^http/.test(url)) {
    p.url = url;
  } else {
    p.imgbase64String = url;
  }
  const res = await invokeAsync('saveImage', p);
  const { errCode, errMsg, result } = res;
  return wrapResult(errCode, errMsg, result);
}

const collectActionType = {
  query: 2,
};
// 收藏
export async function collectAction(param: { type: number; cid: string }): Promise<IResult> {
  const { type, cid } = param;
  if (!cid) {
    return wrapResult(resultCode.error, `${resultMessage.error}(cid empty)`);
  }
  const attentKey = `attent_type=2&rid=${cid}`;
  if (type === collectActionType.query) {
    const queryResult = await invokeAsync('queryAttention', {
      data: [
        {
          attentKey,
          cid,
        },
      ],
    });
    const { errCode, errMsg, result } = queryResult;
    const collected = result.length > 0 ? Number(result[0].attentState) === 1 : false;
    return wrapResult(errCode, errMsg, {
      collected,
      data: result,
    });
  }
  const attentState = type;
  const apiParam = {
    attentKey,
    attentState,
    attentType: '1',
  };
  const changeResult = await invokeAsync('changeAttention', apiParam);
  const { errCode, errMsg } = changeResult;
  const collected = Number(attentState) === 0 && errCode === 0;
  return wrapResult(errCode, errMsg, {
    collected,
    data: [
      {
        attentState: collected ? '1' : '0',
      },
    ],
  });
}

// 关闭
export async function close(): Promise<IResult> {
  return mobileClose();
}

// 获取用户信息
export async function getUserInfo(param: { data: any; isTest: boolean }): Promise<IResult> {
  return mobileGetUserInfo(param);
}

// 获取用户设备id
export async function getDeviceId(): Promise<IResult> {
  return mobileGetDeviceId();
}

// 获取亮度
export async function getBrightness(): Promise<IResult> {
  const res = await invokeAsync('getBrightness', null);
  const { result } = res;
  return wrapResult(resultCode.ok, resultMessage.ok, result);
}

// 设置亮度
export async function setBrightness(param: { brightness: number }): Promise<IResult> {
  await invokeAsync('setBrightness', param);
  return wrapResult(resultCode.ok, resultMessage.ok, {
    brightness: param.brightness,
  });
}

let noSleepInstance: any = null;
let noSleepInterval: any;
// 设置屏幕熄屏
export async function setScreenSleep(param: {
  noSleep: boolean;
  force: boolean;
}): Promise<IResult> {
  const { noSleep, force } = param;
  const isNativeSupport = await invokeAsync('checkApi', {
    apiList: ['keepSystemScreenOnSwitch'],
  });
  if (isNativeSupport.result.keepSystemScreenOnSwitch) {
    const result = await invokeAsync('keepSystemScreenOnSwitch', {
      isKeepOn: noSleep,
    });
    const { errCode, errMsg } = result;
    if (errCode === 0) {
      return wrapResult(resultCode.ok, resultMessage.ok);
    }
    return wrapResult(resultCode.error, `${resultMessage.error}: ${errMsg}`);
  }
  if (force || (isHuawei && isTBS)) {
    clearInterval(noSleepInterval);
    if (!noSleepInstance) {
      const NoSleep = await loadNoSleepModule();
      noSleepInstance = new NoSleep();
    }
    // 如果需要禁止熄屏，那么设置每隔5秒间断性调用一次 nosleep.disable 和 nosleep.enable
    if (param.noSleep) {
      noSleepInstance.enable();
      noSleepInterval = setInterval(() => {
        noSleepInstance.disable();
        setTimeout(() => {
          noSleepInstance.enable();
        }, 1000);
      }, 5000);
    } else {
      noSleepInstance.disable();
    }
    return wrapResult(resultCode.ok, resultMessage.ok);
  }
  return wrapResult(resultCode.notSupport, resultMessage.notSupport);
}

// 获取声音
export async function getVolume(): Promise<IResult> {
  const res = await invokeAsync('getVolume', null);
  const { result } = res;
  return wrapResult(resultCode.ok, resultMessage.ok, result);
}

// 设置声音
export async function setVolume(param: { volume: number }): Promise<IResult> {
  await invokeAsync('setVolume', param);
  return wrapResult(resultCode.ok, resultMessage.ok, {
    volume: param.volume,
  });
}

// 获取网络状态
export async function getNetworkState(): Promise<IResult> {
  return mobileGetNetworkState();
}

// 打开webview
export async function openWebview(param: {
  url: string;
  close?: number;
  style?: number;
}): Promise<IResult> {
  return mobileOpenWebview(param);
}

// 打开支付页
export async function openPayPage(param: {
  appid?: string;
  actid?: string;
  orderid?: string;
  needpay?: string;
  close?: number;
  ru?: string;
  title?: string;
  sandbox?: number;
  autopay?: number;
}): Promise<IResult> {
  return mobileOpenPayPage(param);
}

// 刷新标题
export async function refreshTitle(param: { title: string }): Promise<IResult> {
  if (isAndroid) {
    document.title = param.title;
    return wrapResult(resultCode.ok, resultMessage.ok);
  }
  const res = await invokeAsync('refreshTitle', param);
  const { errCode, errMsg, result } = res;
  return wrapResult(errCode, errMsg, result);
}

// 扫描二维码
export async function scanQRCode(): Promise<IResult> {
  const res = await invokeAsync('scanQRCode', null);
  const { result, qrUrl } = res;
  let code = resultCode.ok;
  let msg = resultMessage.ok;
  if (result === 2) {
    code = resultCode.cancel;
    msg = resultMessage.cancel;
  } else if (result === 0) {
    code = resultCode.error;
    msg = resultMessage.error;
  }
  return wrapResult(code, msg, {
    url: qrUrl || '',
  });
}

// 设置旋转
export async function setRequestedOrientation(param: { orientation: number }): Promise<IResult> {
  // ios不会回调, 默认返回ok就好，延迟调用旋转
  setTimeout(() => {
    invokeAsync('setRequestedOrientation', param);
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 震动设备
export async function shakeDevice(param: { type: string }): Promise<IResult> {
  const { type } = param;
  if (type === 'short') {
    invokeAsync('doVibratorShort', null);
  } else {
    invokeAsync('doVibratorLong', null);
  }
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 分享
export async function share(param: {
  title: string;
  subTitle?: string;
  url: string;
  imageUrl: string;
  sharePageTitle?: string;
  sharePageLaunchUrl?: string;
}): Promise<IResult> {
  return mobileShare(param);
}

// 分享图片
export async function shareImage(param: {
  imageUrl?: string;
  localImageUrl?: string;
  title?: string;
  subTitle?: string;
  needPreview?: boolean;
}): Promise<IResult> {
  const { imageUrl, localImageUrl, title = '', subTitle = '' } = param;
  let localImageUrlData;
  if (localImageUrl) {
    localImageUrlData = localImageUrl.replace(/^data:image\/(png|jpg|jpeg|gif);base64,/i, '');
  }
  if (param.needPreview) {
    const shareSource = await previewShareImage({
      image: localImageUrl || imageUrl,
    });
    if (shareSource) {
      await invokeAsync(shareSource, {
        title,
        subTitle,
        imageUrl,
        isShareImageOnly: true,
        imgbase64String: localImageUrlData,
      });
      return wrapResult(resultCode.ok, resultMessage.ok);
    }
    return wrapResult(resultCode.cancel, resultMessage.cancel);
  }
  const res = await invokeAsync('shareLocalImage', {
    title,
    subTitle,
    imageUrl,
    imgbase64String: localImageUrlData,
  });
  const { errCode } = res;
  if (errCode === 0) {
    return wrapResult(resultCode.ok, resultMessage.ok);
  }
  return wrapResult(resultCode.error, `${resultMessage.error}(${errCode})`);
}

// 隐藏/显示左上角系统返回按钮
export async function toggleBackButton(param: { hide: boolean }): Promise<IResult> {
  const res = await invokeAsync('hideBackButton', param);
  return wrapResult(res.errCode, res.errMsg, res.result);
}

// 写日志
export async function writeLog(param: {
  appid: string;
  content: string;
  isReportServer: boolean;
  isConsoleLog: boolean;
}): Promise<IResult> {
  return mobileWriteLog(param);
}

// 打开VIP开通页/浮层
export async function openPayVipView(param: { url?: string }) {
  return mobileOpenPayVipView(param);
}

// toast
export async function toast(content: string) {
  return mobileToast(content);
}

// showH5
export async function showH5() {
  const res = await invokeAsync('showH5', null);
  return wrapResult(res.errCode, res.errMsg, res.result);
}

// hideH5
export async function hideH5() {
  const res = await invokeAsync('hideH5', {
    isShowControl: 0,
  });
  return wrapResult(res.errCode, res.errMsg, res.result);
}
